import moment from "moment";
import Signal from "signals";
import { get } from "svelte/store";
import AlertType from "../Alerts/AlertType";
import AlertsStore from "../Alerts/Store";
import IdentityHelper from "../Branding/IdentityHelper";
import ColorHelper from "../Helpers/ColorHelper";
import GeoHelper from "../Helpers/GeoHelper";
import { calcLocalTimeFromPoint, formatTime } from "../Helpers/Misc/timeHelper";
import IconMode from "../Map/Enums/IconMode";
import BaseModel from "./BaseModel";
import VesselAlertDataPropertyMap from "./Enums/VesselAlertDataPropertyMap";
import VesselIcon from "./Enums/VesselIcon";
import VesselProperty from "./Enums/VesselProperty";
import VesselTypeCategoryMeta from "./Enums/VesselTypeCategoryMeta";
import ModelType from "./ModelType";
import Path from "./Path";
import VesselGroup from "./VesselGroup";
import VesselPathConfig from "./VesselPathConfig";

class Vessel {
	constructor(data, alertType, alertCategory = null, group = null, map = VesselAlertDataPropertyMap) {
		// WARNING! this._feature should be used internally, all external use should rely on deep copy from this.feature
		// - additionally references to the properties object as a whole should be done via this.feature.properties
		// otherwise unexpected behaviour can occur
		if (!group) {
			group = new VesselGroup();
		}
		if (!data) {
			data = GeoHelper.featureAt(0, 0, {
				[map[VesselProperty.TIMESTAMP]]: 0,
			});
		}
		if (!alertType) {
			alertType = AlertType.NONE;
		}
		if (!alertCategory) {
			alertCategory = AlertType.DEFAULT_CATEGORY_NAME;
		}

		this.type = ModelType.VESSEL;
		this.group = group;
		this.map = map;
		this._feature = {
			type: "Feature",
			properties: {
				iconImage: null,
				alertTypes: [],
				alertCategories: [],
			},
			geometry: {
				type: "Point",
				coordinates: data.geometry.coordinates,
			},
		};

		// Fix timestamp into UTC if it's not.
		if (data.properties[map[VesselProperty.TIMESTAMP]]) {
			// if a timestamp comes through without the Z on the end (the time stamp is only 19 long) we add a Z
			if (data.properties[map[VesselProperty.TIMESTAMP]].length === 19) {
				data.properties[map[VesselProperty.TIMESTAMP]] += "Z";
			}
		}

		// See comment for BaseModel.remap.
		BaseModel.remap(this._feature, data, VesselProperty, this.map);

		// Vessel unique id property.
		this.setUniqIdType(VesselProperty.IMO);

		this._focused = false;
		this._selected = false;
		this._visible = false;
		this._historicPath = null;
		this._hasHistoricData = false;
		this.onSelectedChanged = new Signal();

		this.registerAlertType(alertType, alertCategory);
		this._feature.properties.iconImage = setIconImage(this, get(AlertsStore.iconMode));

		// E.g. usually from mmsi changes.
		this._previousVersions = [];

		this._hasEnhancedData = false;
		this._enhancedData = null;

		this.updateSnapshot();
	}

	addPreviousVersion(vessel) {
		this._previousVersions.push(vessel);
	}

	get previousVersions() {
		return this._previousVersions;
	}

	get previousVersionDiffs() {
		const propertiesToCheck = [
			VesselProperty.NAME,
			VesselProperty.MMSI,
			VesselProperty.FLAG,
			VesselProperty.WIDTH,
			VesselProperty.LENGTH,
			VesselProperty.TIMESTAMP,
		];
		const diffs = [];
		for (let i = 0; i < this._previousVersions.length; i++) {
			const thisDiff = {};
			const keys = propertiesToCheck; // Object.keys(this._feature.properties);
			for (let j = 0; j < keys.length; j++) {
				const key = keys[j];
				const oldVal = this._previousVersions[i]._feature.properties[key];
				if (this._feature.properties[key] !== oldVal) {
					thisDiff[key] = oldVal;
				}
			}
			diffs.push(thisDiff);
		}
		return diffs;
	}

	// type = VesselProperty.
	// value = value, but can be null to default.
	setUniqIdType(type, value = null) {
		this.uniqidType = type;
		this.uniqid = value === null ? this[this.uniqidType] : value;
	}

	generateHistoricPath(messages) {
		// Remap from Alerts/Spire properties to internal property names.
		const newMessages = messages.map((messageFeature) => {
			const obj = GeoHelper.featureAt(
				messageFeature.geometry.coordinates[0],
				messageFeature.geometry.coordinates[1]
			);
			BaseModel.remap(obj, messageFeature, VesselProperty, this.map);
			return obj;
		});
		this._historicPath = new Path(this, newMessages, VesselPathConfig);

		// Sometimes historic/messages data is more recent than 'live' data. Account for that here!
		// We edit the live data properties and coordinates with the latest from historic.
		// https://trello.com/c/6fPq91rU/284-messages-data-is-sometimes-more-recent-that-alerts-data
		const mostRecentStop = this._historicPath.stops._stopArray[this._historicPath.stops._stopArray.length - 1];
		if (
			mostRecentStop &&
			mostRecentStop.properties[VesselProperty.TIMESTAMP] > this._feature.properties[VesselProperty.TIMESTAMP]
		) {
			for (const key in mostRecentStop.properties) {
				this._feature.properties[key] = mostRecentStop.properties[key];
			}
			this._feature.geometry.coordinates = mostRecentStop.geometry.coordinates;
			this.updateSnapshot();
		}

		this._hasHistoricData = messages.length > 0;
	}

	hasHistoricData() {
		return this._hasHistoricData;
	}

	getDefaultDate() {
		return get(this.group.timeSliderData.value);
	}

	// This is called by AlertsController on all vessels whenever the snapshot slider value changes,
	// afterwhich layerdata is renewed
	updateSnapshot(date = null) {
		if (date === null) {
			date = this.getDefaultDate();
		}

		if (!this.historicPath) {
			this._snapshotFeature = date.isBefore(this.timestamp) ? null : this.feature;
			return;
		}

		const snapshotData = this.historicPath.getSnapshotVesselFeature(date);
		if (!snapshotData) {
			// Calculation attempted but unsuccesful!
			this._snapshotFeature = null;
			return;
		}

		if (!this._snapshotFeature) this._snapshotFeature = this.feature;

		this._snapshotFeature.geometry.coordinates = snapshotData.geometry.coordinates;
		this._snapshotFeature.properties = {
			...this.feature.properties,
			[VesselProperty.TIMESTAMP]: snapshotData.properties[VesselProperty.TIMESTAMP],
			[VesselProperty.FLAG]: snapshotData.properties[VesselProperty.FLAG],
			[VesselProperty.SPEED]: snapshotData.properties[VesselProperty.SPEED],
			[VesselProperty.COURSE]: snapshotData.properties[VesselProperty.COURSE]
				? snapshotData.properties[VesselProperty.COURSE]
				: null,
		};

		setIconImage(this, get(AlertsStore.iconMode));
		if (this.isDirty) {
			this.updateSnapshot(date);
		}
	}

	// TODO: legacy, remove, point to this.feature
	asFeature() {
		console.warn("Please change to vessel.feature rather than vessel.asFeature()");
		return this.feature;
	}

	// Note: Currently no way to remove alert as I can't think of a situation that would happen
	addAlertData(alertData, alertType, alertCategory = "") {
		if (this._feature.properties.alertTypes.includes(alertType)) {
			// console.warn("vessel already includes alertType data. skipping. ", this.mmsi, alertType);
			return;
		}

		// If timestamp newer than current data, depict newer data
		if (alertData.properties[this.map[VesselProperty.TIMESTAMP]] > this.timestamp) {
			this._feature.geometry.coordinates = alertData.geometry.coordinates;
			this._feature.properties[VesselProperty.TIMESTAMP] =
				alertData.properties[this.map[VesselProperty.TIMESTAMP]];
			this._feature.properties[VesselProperty.COURSE] = alertData.properties[this.map[VesselProperty.COURSE]];
			this._feature.properties[VesselProperty.DRAUGHT] = alertData.properties[this.map[VesselProperty.DRAUGHT]];
			this._feature.properties[VesselProperty.SPEED] = alertData.properties[this.map[VesselProperty.SPEED]];

			if (alertType === AlertType.CAS || alertType === AlertType.DEC) {
				this._feature.properties[VesselProperty.FLAG] = alertData.properties[this.map[VesselProperty.FLAG]];
				this._feature.properties[VesselProperty.STATIC_UPDATE_AT] =
					alertData.properties[this.map[VesselProperty.STATIC_UPDATE_AT]];
			}
		}
		this.registerAlertType(alertType, alertCategory);
	}

	registerAlertType(alertType, alertCategory = "") {
		if (alertType === AlertType.NONE) {
			this.visible = true;
			return;
		}

		if (this._feature.properties.alertTypes.includes(alertType)) return;

		this._feature.properties.alertTypes.push(alertType);
		this._feature.properties.alertCategories.push(alertCategory);
		// For change of visibility of newly assigned alert type, check all vessel's alert types,
		// if any are visible, make visible
		// TODO: review if unsubscribe is needed here? We shoudn't be reproducing Vessels,
		//  so I don't think so, but might need to trace?
		const visibilityRefresh = () => {
			let isVisibleForAny = false;
			this._feature.properties.alertTypes.forEach((alertType, index) => {
				let newVisibility = AlertsStore.filters.alertTypes[alertType].isVisible();
				// const countWithName = AlertsStore.filters.alertTypes[alertType].countCategoriesWithName();

				const subcategories = get(AlertsStore.filters.alertTypes[alertType].subcategories);

				const thisAlertTypeSubcategory = this._feature.properties.alertCategories[index];
				// console.log("subcategories", subcategories);
				// console.log("thisAlertTypeSubcategory", thisAlertTypeSubcategory);
				for (let i = 0; i < subcategories.length; i++) {
					if (subcategories[i].name === thisAlertTypeSubcategory) {
						if (!get(subcategories[i].visible)) {
							newVisibility = false;
						}
					}
				}

				// if (AlertsStore.filters.alertTypes[alertType].isVisible()) {
				// newVisibility = true;
				// }
				if (newVisibility) {
					isVisibleForAny = true;
				}
			});

			// console.log("newVisibility", newVisibility);
			this.visible = isVisibleForAny;
		};

		// Register events
		AlertsStore.filters.alertTypes[alertType].visible.subscribe(visibilityRefresh);

		const subcategories = get(AlertsStore.filters.alertTypes[alertType].subcategories);
		for (let i = 0; i < subcategories.length; i++) {
			if (subcategories[i].name === alertCategory) {
				subcategories[i].visible.subscribe(visibilityRefresh);
			}
		}
	}

	get hasEnhancedData() {
		return this._hasEnhancedData;
	}

	set enhancedData(data) {
		this._enhancedData = data;
		this._hasEnhancedData = true;
	}

	get enhancedData() {
		return this._enhancedData;
	}

	// Recalculate the iconImage when the mode changes
	// Called by AlertsController
	// - Rather than subscribing directly, iconMode is set by AlertsController to minimise the number of times
	// MapController.UpdateSources has to be called
	setIconMode(iconMode) {
		setIconImage(this, iconMode);
		// TODO: probably doesn't need full snapshot update?
		this.updateSnapshot();
	}

	// region Getters and Setters
	get iconImage() {
		return this._feature.properties.iconImage;
	}

	set iconImage(iconImage) {
		// TODO: figure out why `this._feature.properties.iconImage = iconImage` doesn't work!
		this._feature = {
			...this._feature,
			properties: {
				...this._feature.properties,
				iconImage: iconImage,
			},
		};
	}

	get lineColor() {
		// console.log(this.iconImage);
		return VesselIcon.helpers.toColor(this.iconImage);
	}

	// Returns deep copy, readonly
	// Never allow this to circularly feed anything directly in this._feature
	get feature() {
		return JSON.parse(JSON.stringify(this._feature));
	}

	// Old API support, remove in future, use feature
	get data() {
		console.warn("Deprecated method: Please use vessel.feature instead of vessel.data");
		return this.feature;
	}

	get currentFeature() {
		if (this.hasActiveSnapshot()) {
			return this.snapshotFeature;
		}
		return this.feature;
	}

	// Returns deep copy, readonly
	get snapshotFeature() {
		if (this._snapshotFeature === undefined) {
			this.updateSnapshot();
		}
		if (this._snapshotFeature === null) {
			// console.warn("No history for current snapshot date");
			return false;
		}

		const response = { ...this._snapshotFeature };
		response.geometry = { ...this._snapshotFeature.geometry };
		response.properties = { ...this._snapshotFeature.properties };
		return response;
	}

	// TODO: We will want to rehook the mapping from server data keys to local data keys.
	// This involves hooking back up to BaseModel, and removing AlertProp.
	get course() {
		return this.feature.properties[VesselProperty.COURSE];
	}

	get name() {
		return this._feature.properties[VesselProperty.NAME];
	}

	get imo() {
		return this._feature.properties[VesselProperty.IMO];
	}

	get mmsi() {
		return this._feature.properties[VesselProperty.MMSI];
	}

	get timestamp() {
		return this._feature.properties[VesselProperty.TIMESTAMP];
	}

	get snapshotTimestamp() {
		return this._snapshotFeature.properties[VesselProperty.TIMESTAMP];
	}

	get currentTimestamp() {
		return this.hasActiveSnapshot() ? this.snapshotTimestamp : this.timestamp;
	}

	get flag() {
		return this._feature.properties[VesselProperty.FLAG];
	}

	get classification() {
		return this._feature.properties[VesselProperty.CLASSIFICATION];
	}

	get draught() {
		return this._feature.properties[VesselProperty.DRAUGHT];
	}

	get speed() {
		return this._feature.properties[VesselProperty.SPEED];
	}

	get speedKnots() {
		return Vessel.formatKnots(this.speed);
	}

	get snapshotMmsi() {
		return this._snapshotFeature.properties[VesselProperty.MMSI];
	}

	get snapshotFlag() {
		if (this._snapshotFeature && this._snapshotFeature.properties[VesselProperty.FLAG]) {
			return this._snapshotFeature.properties[VesselProperty.FLAG];
		}
		return this.flag;
	}

	get snapshotCourse() {
		if (this._snapshotFeature) {
			return this._snapshotFeature.properties[VesselProperty.COURSE];
		}
		return null;
	}

	get snapshotSpeed() {
		return this._snapshotFeature.properties[VesselProperty.SPEED];
	}

	get snapshotSpeedKnots() {
		return Vessel.formatKnots(this.snapshotSpeed);
	}

	get snapshotCoordinates() {
		return this._snapshotFeature.geometry.coordinates;
	}

	get snapshotLocalTime() {
		const pointLocalTime = calcLocalTimeFromPoint(this.snapshotCoordinates);
		return pointLocalTime != null ? pointLocalTime : "<em>[unavailable]</em>";
	}

	get voyageDestination() {
		return this._feature.properties[VesselProperty.VOYAGE_DESTINATION];
	}

	get voyageEta() {
		return this._feature.properties[VesselProperty.VOYAGE_ETA];
	}

	get spireId() {
		// return "ff771b5e-3a46-4978-94c4-9ae541e9c671";
		return this._feature.properties[VesselProperty.SPIRE_ID];
	}

	get coordinates() {
		return this._feature.geometry.coordinates;
	}

	get currentCoordinates() {
		if (this.hasActiveSnapshot()) {
			return this._snapshotFeature.geometry.coordinates;
		}
		return this.coordinates;
	}

	get lat() {
		return this.coordinates[1];
	}

	get lon() {
		return this.coordinates[0];
	}

	get snapshotLng() {
		return this.snapshotCoordinates[0];
	}

	get snapshotLat() {
		return this.snapshotCoordinates[1];
	}

	get lng() {
		return this.lon;
	}

	get update() {
		return this._feature.properties[VesselProperty.UPDATE];
	}

	get callSign() {
		return this._feature.properties[VesselProperty.CALL_SIGN];
	}

	get createdAt() {
		return this._feature.properties[VesselProperty.CREATED_AT];
	}

	get nameOrFlagChangedTimestamp() {
		if (this._feature.properties[VesselProperty.STATIC_UPDATE_AT]) {
			return this._feature.properties[VesselProperty.STATIC_UPDATE_AT];
		}
		// console.warn("Flag change data requested on non CAS alert type");
		return false;
	}

	get alertTypes() {
		// Copy to keep actual array internal
		const returnArray = [];
		this._feature.properties.alertTypes.forEach((alert) => returnArray.push(alert));
		return returnArray;
	}

	get alertCategories() {
		// Copy to keep actual array internal
		const returnArray = [];
		this._feature.properties.alertCategories.forEach((alert) => returnArray.push(alert));
		return returnArray;
	}

	get alertType() {
		console.error("alertType is now invalid! Please iterate across alertTypes or default to alertType[0]");
		return false;
	}

	get localTime() {
		const pointLocalTime = calcLocalTimeFromPoint(this.coordinates);
		return pointLocalTime != null ? pointLocalTime : "<em>[unavailable]</em>";
	}

	get extraData() {
		return this.feature.properties[VesselProperty.EXTRA];
	}

	isSTS() {
		return this.alertTypes.indexOf(AlertType.STS) >= 0 && this.getSTSData();
	}

	getSTSData() {
		return this.extraData.sts;
	}

	getSTSPartner() {
		return this.group.find({ mmsi: this.getSTSData().for });
	}

	isRIM() {
		return this.alertTypes.indexOf(AlertType.RIM) >= 0 && this.getRIMData();
	}

	getRIMData() {
		return this.extraData.risk;
	}

	getRiskLevel() {
		return this.getRIMData().level;
	}

	hasStrikeData() {
		return typeof this.extraData.strike !== "undefined" && this.extraData.strike !== null;
	}

	getStrikeData() {
		return this.extraData.strike;
	}

	isNearbyAlert() {
		return this.alertTypes.indexOf(AlertType.NBY) >= 0 && this.getNearbyAlertData();
	}

	getNearbyAlertData() {
		return this.extraData ? this.extraData.vessel_nearby : null;
	}

	get selected() {
		return this._selected;
	}

	set selected(value) {
		this._selected = value;
		setIconImage(this, get(AlertsStore.iconMode));
		// TODO: Should this be called by the thing that calls `isSelected` instead of being inside? Or is
		//  there a way to wait a little bit (a resetting warm-up?) on the action to allow other calls?
		// TODO: Vessels should probably belong to a 'group' i.e. alerts, vessel history, vessels nearby
		// 	query, instead of always triggering something in AlertsStore. (^AG)
		// this.addOrRemovePropertyToWritableList("imo", AlertsStore.selection.vesselImos, value);
		this.addOrRemovePropertyToWritableList(this.uniqidType, this.group.selected[this.uniqidType], value);
		this.onSelectedChanged.dispatch(value);
		// TODO: probably doesn't need full snapshot update?
		this.updateSnapshot();
	}

	set focused(value) {
		this._focused = value;
		// We set the focused state on the group.
		this.addOrRemovePropertyToWritableList(this.uniqidType, this.group.focused[this.uniqidType], value);
	}

	get focused() {
		return this._focused;
	}

	addOrRemovePropertyToWritableList(property, list, value) {
		const lst = get(list);
		if ((lst.indexOf(this[property]) >= 0 && value) || (lst.indexOf(this[property]) === -1 && !value)) {
			return;
		}

		list.update((existing) => {
			if (value) {
				return [...existing, this[property]];
			} else {
				return existing.filter((existingData) => existingData !== this[property]);
			}
		});
	}

	get visible() {
		return this._visible;
	}

	set visible(value) {
		this._visible = value;
	}

	get historicPath() {
		return this._historicPath;
	}

	get indexInGroup() {
		const groupVessels = get(this.group.vessels);
		return Math.max(groupVessels.indexOf(this), 0);
	}

	set historicPath(path) {
		console.warn("Please use `generageHistoricPath` rather than setting directly");
		this._historicPath = path;
	}

	hasActiveSnapshot() {
		return (
			this._snapshotFeature &&
			this._snapshotFeature.properties[VesselProperty.TIMESTAMP] <
				this._feature.properties[VesselProperty.TIMESTAMP]
		);
	}

	// endregion (getters and setters)
} // Vessel

// TODO: Consider extra readonly getters for timestampFormatted, etaFormatted.
//  Could allow user-customisable formats.
Vessel.SPIRE_SPEED_UNKNOWN = 102.3;

Vessel.formatTime = (isoTime) => formatTime(isoTime);
Vessel.formatCourse = (course) => course || "Unknown";
Vessel.formatUndefined = (value) => value || "Unknown";
Vessel.formatDraught = (value) => value + " m" || "Unknown";
Vessel.formatAltitude = (value) => value + " ft" || "Unknown";
Vessel.formatKnots = (speed) => {
	return typeof speed === "number" ? (speed !== 102.3 ? speed + " knots" : "Unknown") : "Unknown";
};
Vessel.formatCourseHeading = (course, heading) => {
	let str = "";
	str += course || "Unknown";
	str += " / ";
	str += heading || "Unknown";
	return str;
};
Vessel.formatDegrees = (value) => {
	return typeof value === "number" ? value + " degrees" : "Unknown";
};
Vessel.formatProperty = (value, property) => {
	if (property === VesselProperty.SPEED) {
		if (!Number.isNaN(parseFloat)) {
			value = parseFloat(value);
		}
		return Vessel.formatKnots(value);
	} else if (property === VesselProperty.COURSE || property === VesselProperty.HEADING) {
		if (!Number.isNaN(parseFloat)) {
			value = parseFloat(value);
		}
		return Vessel.formatDegrees(value);
	} else if (property === VesselProperty.DRAUGHT) {
		return Vessel.formatDraught(value);
	} else if (property === VesselProperty.TIMESTAMP) {
		return Vessel.formatTime(value);
	}
	return Vessel.formatUndefined(value);
};

const setIconImage = (vessel, iconMode) => {
	vessel.isDirty = false;
	const prevIcon = vessel.iconImage;
	let baseIcon = VesselIcon.WHITE;
	if (iconMode === IconMode.ALERT) {
		const alertConfigs = IdentityHelper.current().alertConfigs;
		let match = false;
		for (let j = 0; j < vessel.alertTypes.length; j++) {
			const alertType = vessel.alertTypes[j];
			const alertTypeSubcategory = vessel.alertCategories[j];
			if (
				!AlertsStore.filters.alertTypes[alertType].isVisible() ||
				!AlertsStore.helpers.isAlertTypeSubcategoryVisible(alertType, alertTypeSubcategory)
			) {
				continue;
			}
			for (let i = 0; i < alertConfigs.length; i++) {
				const alertConfig = alertConfigs[i];

				if (vessel.alertTypes[j] === alertConfig.alertType) {
					baseIcon = VesselIcon[alertConfig.vesselColour];
					match = true;
					break;
				}
			}
			if (match) {
				break;
			}
		}
		if (!match) {
			// It's probably set to not be visible.
			// console.log("could not color vessel", vessel.mmsi, vessel.alertTypes, vessel.alertCategories);
			// console.warning("Alert type not found in config", vessel.imo, vessel.alertTypes, alertConfigs);
			/* for (let j = 0; j < vessel.alertTypes.length; j++) {
				const alertType = vessel.alertTypes[j];
				const alertTypeSubcategory = vessel.alertCategories[j];
				console.log("alertType visible", alertType, AlertsStore.filters.alertTypes[alertType].isVisible());
				console.log("alertTypeCategory visible", alertType, alertTypeSubcategory, AlertsStore.helpers.isAlertTypeSubcategoryVisible(alertType, alertTypeSubcategory));
			}*/
		}
	} else if (iconMode === IconMode.TIME_DIFFERENCE || iconMode === IconMode.TIME_SINCE_PREVIOUS_UPDATE) {
		const time = vessel.currentTimestamp;
		let hoursSinceUpdate = 0;
		// Coloured by the difference between the slider time and the closest historic point previous to it.
		if (iconMode === IconMode.TIME_DIFFERENCE) {
			const sliderVal = get(vessel.group.timeSliderData.value);
			hoursSinceUpdate = moment(sliderVal).diff(moment(time), "hours");
		} else if (iconMode === IconMode.TIME_SINCE_PREVIOUS_UPDATE) {
			hoursSinceUpdate = 0; // TODO: this! See Iconmode for complications to consider.
		}

		if (hoursSinceUpdate <= 8) baseIcon = VesselIcon.LIGHTGREEN;
		else if (hoursSinceUpdate <= 16) baseIcon = VesselIcon.ORANGE;
		else if (hoursSinceUpdate <= 24) baseIcon = VesselIcon.LIGHTRED;
		else if (hoursSinceUpdate <= 168) baseIcon = VesselIcon.DARKBLUE; // 168 hours === 1 week
		else baseIcon = VesselIcon.BLACK;
	} else if (iconMode === IconMode.SPEED) {
		let speed = vessel.speed;
		if (vessel.hasActiveSnapshot()) {
			speed = vessel.snapshotSpeed;
		}
		if (typeof speed === "string" || speed === Vessel.SPIRE_SPEED_UNKNOWN) {
			baseIcon = VesselIcon.BLACK;
		} else if (speed < 0.1) baseIcon = VesselIcon.LIGHTGREEN;
		else if (speed >= 0.1 && speed < 10) baseIcon = VesselIcon.ORANGE;
		else if (speed >= 10 && speed < 18) baseIcon = VesselIcon.LIGHTRED;
		else if (speed >= 18) baseIcon = VesselIcon.PURPLE;
		else baseIcon = VesselIcon.BLACK;
	} else if (iconMode === IconMode.VESSEL_TYPE) {
		const classification = vessel.classification;

		let found = false;
		const keys = Object.keys(VesselTypeCategoryMeta);
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i];
			if (VesselTypeCategoryMeta[key].contains.indexOf(classification) >= 0) {
				baseIcon = VesselIcon[VesselTypeCategoryMeta[key].color];
				found = true;
				break;
			}
		}
		if (!found) {
			baseIcon = VesselIcon.BLACK; // Unknown/other
		}
		// TODO: factor in old VesselType enum?
	} else if (iconMode === IconMode.INDEX) {
		const keys = Object.keys(VesselIcon);
		keys.splice(0, 1); // Remove WHITE
		keys.splice(keys.length - 1, 1); // Remove "helpers"
		baseIcon = VesselIcon[keys[vessel.indexInGroup % keys.length]];
		// console.log("indexInGroup", vessel.indexInGroup, "baseIcon", baseIcon);
	} else if (iconMode === IconMode.RISK_LEVEL) {
		const level = vessel.isRIM() ? parseInt(vessel.getRiskLevel()) : 0;
		const col = ColorHelper.getRiskLevelVesselColor(level);
		baseIcon = VesselIcon[col];
	}
	vessel.iconImage = vessel.selected ? baseIcon.highlight : baseIcon.standard;
	if (
		(vessel.hasActiveSnapshot() && vessel.snapshotCourse == null) ||
		(!vessel.hasActiveSnapshot() && vessel.course == null)
	) {
		vessel.iconImage = vessel.selected ? baseIcon.circleHighlight : baseIcon.circleStandard;
	}
	if (prevIcon !== vessel.iconImage) {
		vessel.isDirty = true;
	}
};

export default Vessel;
